import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { Dropdown, Button, Divider } from 'antd';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import { LocalizationProvider, DatePicker, MobileTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { InputAdornment } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import TextField from '@mui/material/TextField';
import { formatBrowser } from '../utils/dateUtils';

const menuBg = '#fff';

const PeriodRangePicker = forwardRef(({
  value,
  onChange,
  showCustom,
  setPreset,
  setShowCustom,
  selectedPreset,
  selectedLabel,
  presetDefs,
  width = '100%'
}, ref) => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);

  useImperativeHandle(ref, () => ({
    getRange: () => value,
    isCustom: showCustom
  }));

  const fromStr = value.from ? formatBrowser(value.from) : '';
  const toStr = value.to ? formatBrowser(value.to) : '';

  const dropdownOverlay = (
    <div
      style={{
        minWidth: 320,
        background: menuBg,
        borderRadius: 4,
        boxShadow: '0 8px 24px #0001',
        padding: 4
      }}
    >
      <div>
        {presetDefs.map(preset => {
          const key = preset.key;
          const isSelected = (!showCustom && selectedPreset === key) || (key === 'custom' && showCustom);
          const isHovered = hoveredKey === key;
          return (
            <div
              key={key}
              style={{
                padding: '7px 16px',
                cursor: 'pointer',
                // background: isSelected ? '#F0F7FA' : menuBg,
                background: isSelected ? '#F0F7FA' : isHovered ? '#f5f5f5': menuBg,
                fontWeight: isSelected ? 600 : 400,
                color: key === 'custom' && showCustom ? '#0075C9' : '#222',
                display: 'flex',
                alignItems: 'center',
                borderRadius: 6
              }}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={e => {
                e.stopPropagation();
                setPreset(key);
                if (key !== 'custom') setOverlayOpen(false);
              }}
              onMouseDown={e => e.preventDefault()}
            >
              {isSelected && <CheckOutlined style={{ color: '#0075C9', marginRight: 7, fontSize: 13 }} />}
              {preset.text}
            </div>
          );
        })}
      </div>

      {showCustom && (
        <>
          <Divider style={{ margin: '10px 0 8px 0' }} />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ background: '#fafcff', borderRadius: 7, padding: '10px 8px', marginBottom: 2 }}>
              <div style={{ marginBottom: 7, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 34, fontWeight: 500, fontSize: 13, color: '#0075C9' }}>From</span>
                <DatePicker
                  value={value.from}
                  onChange={date => onChange({ ...value, from: date })}
                  format="DD/MM/YYYY" // <--- Dubái
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    textField: {
                      size: 'small',
                      placeholder: 'DD/MM/YYYY', // <--- Dubái
                      sx: {
                        minWidth: 140,  // reduce más el ancho
                        width: 140,
                        '& input': {
                          padding: '6px', // más compacto
                          fontSize: 14,
                        },
                      }
                    }
                  }}
                />
                <MobileTimePicker
                  ampm={false}
                  value={value.from}
                  onOpen={() => {
                    setOverlayOpen(false);   // cerrar dropdown
                  }}
                  onAccept={(newTime) => {
                    if (!newTime) return;

                    onChange({
                      ...value,
                      from: value.from
                        .hour(newTime.hour())
                        .minute(newTime.minute())
                    });
                  }}
                  onClose={() => {
                    // reabrir dropdown después de que el dialog se cierre
                    setTimeout(() => {
                      setOverlayOpen(true);
                    }, 150);   // pequeño delay necesario
                  }}
                  format="HH:mm" // <--- 24h Dubái
                  slotProps={{
                    textField: {
                      size: 'small',
                      placeholder: 'HH:mm', // <--- 24h Dubái
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.54)', flexShrink: 0 }} />
                          </InputAdornment>
                        ),
                      },
                      sx: {
                        minWidth: 95,
                        width: 95,
                        '& input': {
                          padding: '6px',
                          fontSize: 14,
                          textAlign: 'right',
                        },
                      }
                    }
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 34, fontWeight: 500, fontSize: 13, color: '#0075C9' }}>To</span>
                <DatePicker 
                  value={value.to}
                  onChange={date => onChange({ ...value, to: date })}
                  format="DD/MM/YYYY"
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    textField: {
                      size: 'small',
                      placeholder: 'DD/MM/YYYY',
                      sx: {
                        minWidth: 140,  // reduce más el ancho
                        width: 140,
                        '& input': {
                          padding: '6px', // más compacto
                          fontSize: 14,
                        },
                      }
                    }
                  }}
                />
                <MobileTimePicker
                  ampm={false}
                  value={value.to}
                  onOpen={() => {
                    setOverlayOpen(false);   // cerrar dropdown
                  }}
                  onAccept={(newTime) => {
                    if (!newTime) return;

                    onChange({
                      ...value,
                      from: value.from
                        .hour(newTime.hour())
                        .minute(newTime.minute())
                    });
                  }}
                  onClose={() => {
                    // reabrir dropdown después de que el dialog se cierre
                    setTimeout(() => {
                      setOverlayOpen(true);
                    }, 150);   // pequeño delay necesario
                  }}
                  format="HH:mm"
                  slotProps={{
                    textField: {
                      size: 'small',
                      placeholder: 'HH:mm',
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.54)', flexShrink: 0 }} />
                          </InputAdornment>
                        ),
                      },
                      sx: {
                        minWidth: 95,
                        width: 95,
                        '& input': {
                          padding: '6px',
                          fontSize: 14,
                          textAlign: 'right',
                        },
                      }
                    }
                  }}
                />
              </div>
              <div style={{ textAlign: 'right', marginTop: 10 }}>
                <Button size="small" type="primary" onClick={() => setOverlayOpen(false)}>
                  Apply
                </Button>
              </div>
            </div>
          </LocalizationProvider>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      open={overlayOpen}
      onOpenChange={setOverlayOpen}
      dropdownRender={() => dropdownOverlay}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Button
        size="small"
        style={{
          width,
          height: 32,
          padding: '0 11px',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {showCustom ? `${fromStr} → ${toStr}` : selectedLabel}
        <DownOutlined />
      </Button>
    </Dropdown>
  );
});

export default PeriodRangePicker;
